import apiService from "@/services/apiService";

export type EmergencyCodeCategory = "primary_emergency" | "environmental_operational";
export type EmergencyCodeActivationStatus = "active" | "resolved" | "cancelled";

export interface EmergencyCode {
  id: number;
  name: string;
  category: EmergencyCodeCategory;
  color_hex: string;
  description?: string | null;
  default_responder_roles?: string[] | null;
  sort_order: number;
  is_active: boolean;
}

export interface EmergencyCodeActivation {
  id: number;
  code_id: number;
  code: EmergencyCode;
  location: string;
  status: EmergencyCodeActivationStatus;
  notes?: string | null;
  triggered_by: number;
  triggered_at: string;
  resolved_by?: number | null;
  resolved_at?: string | null;
  attended_by_id?: number | null;
  attended_by_name?: string | null;
  attended_by_role?: string | null;
  attended_at?: string | null;
}

export interface EmergencyCodeDelivery {
  id: number;
  activation_id: number;
  channel: "in_app" | "vendor_pa" | "vendor_page";
  recipient_user_id?: number | null;
  recipient_role?: string | null;
  status: "queued" | "sent" | "failed" | "acked";
  error_message?: string | null;
  sent_at?: string | null;
  acked_at?: string | null;
}

export interface CreateEmergencyCodePayload {
  name: string;
  category: EmergencyCodeCategory;
  color_hex: string;
  description?: string;
  default_responder_roles?: string[];
  sort_order?: number;
  is_active?: boolean;
}

export type UpdateEmergencyCodePayload = Partial<CreateEmergencyCodePayload>;

class EmergencyCodeService {
  async receipt(id: number, event: 'received' | 'acknowledged' | 'silenced') {
    let device = localStorage.getItem('daafya_device_id');
    if (!device) { device = crypto.randomUUID(); localStorage.setItem('daafya_device_id', device); }
    return apiService.post(`/emergency-codes/activations/${id}/receipt`, { device_id: device, event });
  }
  async events(id: number) {
    const response = await apiService.get<{ data: any[] }>(`/emergency-codes/activations/${id}/events`);
    return response.data;
  }
  async listCodes(filters?: { category?: EmergencyCodeCategory; is_active?: boolean }) {
    const response = await apiService.get<{ data: EmergencyCode[] }>("/emergency-codes", filters);
    return response.data;
  }

  async createCode(payload: CreateEmergencyCodePayload) {
    const response = await apiService.post<{ data: EmergencyCode }>("/emergency-codes", payload);
    return response.data;
  }

  async updateCode(id: number, payload: UpdateEmergencyCodePayload) {
    const response = await apiService.put<{ data: EmergencyCode }>(`/emergency-codes/${id}`, payload);
    return response.data;
  }

  async removeCode(id: number) {
    await apiService.delete(`/emergency-codes/${id}`);
  }

  async activate(payload: { code_id: number; location: string; notes?: string }) {
    const response = await apiService.post<{ data: EmergencyCodeActivation }>(
      "/emergency-codes/activations",
      payload,
    );
    return response.data;
  }

  async listActive() {
    const response = await apiService.get<{ data: EmergencyCodeActivation[] }>(
      "/emergency-codes/activations/active",
    );
    return response.data;
  }

  async listHistory(filters?: { status?: EmergencyCodeActivationStatus; code_id?: number }) {
    const response = await apiService.get<{ data: EmergencyCodeActivation[] }>(
      "/emergency-codes/activations",
      filters,
    );
    return response.data;
  }

  async listDeliveries(activationId: number) {
    const response = await apiService.get<{ data: EmergencyCodeDelivery[] }>(
      `/emergency-codes/activations/${activationId}/deliveries`,
    );
    return response.data;
  }

  async attend(id: number) {
    const response = await apiService.put<{ data: any }>(
      `/emergency-codes/activations/${id}/attend`
    );
    return response.data;
  }

  async resolve(id: number, payload: { status: "resolved" | "cancelled"; notes?: string }) {
    const response = await apiService.put<{ data: EmergencyCodeActivation }>(
      `/emergency-codes/activations/${id}/resolve`,
      payload,
    );
    return response.data;
  }
}

export default new EmergencyCodeService();
